<template>
  <teleport to="body">
    <!-- 气泡按钮（面板关闭时显示） -->
    <div class="ai-trigger-btn" @click="open" v-show="!visible">
      <Icon name="Bot" :size="24" />
    </div>

    <!-- 侧边面板 -->
    <transition name="ai-slide">
      <div class="ai-sidebar" v-show="visible">
        <!-- 标题栏 -->
        <div class="ai-header">
          <div class="ai-header-left">
            <Icon name="Bot" :size="18" class="ai-header-icon" />
            <span class="ai-header-title">AI 助手</span>
          </div>
          <div class="ai-header-actions">
            <div class="ai-header-action" @click="handleNewSession" title="新建对话">
              <Icon name="Plus" :size="16" />
            </div>
            <div class="ai-header-action" @click="handleDeleteSession" title="删除当前会话">
              <Icon name="Trash2" :size="16" />
            </div>
            <div class="ai-header-action" @click="close" title="关闭">
              <Icon name="X" :size="18" />
            </div>
          </div>
        </div>

        <!-- 会话选择器 -->
        <div class="ai-session-bar">
          <el-select
            v-model="currentSessionId"
            placeholder="选择会话"
            @change="onSessionChange"
            :teleported="false"
            style="width: 100%"
          >
            <el-option
              v-for="s in sessions"
              :key="s.id"
              :label="s.title"
              :value="s.id"
            />
          </el-select>
        </div>

        <!-- 消息列表 -->
        <div class="ai-messages" ref="messagesRef" @click="handleMessagesClick">
          <!-- 欢迎语（无消息时） -->
          <div v-if="messages.length === 0 && !loading" class="ai-welcome">
            <Icon name="Bot" :size="40" class="ai-welcome-icon" />
            <p class="ai-welcome-text">您好，我是 AI 助手，有什么可以帮您的？</p>
            <p class="ai-welcome-sub">您可以点击下方快捷指令，或直接输入问题</p>
          </div>

          <div
            v-for="msg in messages"
            :key="msg.id"
            :class="['ai-message', `ai-message--${msg.role}`]"
          >
            <div v-if="msg.role === 'assistant'" class="ai-message-avatar">
              <Icon name="Bot" :size="14" />
            </div>
            <!-- 用户消息：纯文本 -->
            <div v-if="msg.role === 'user'" class="ai-message-content">{{ msg.content }}</div>
            <!-- 助手消息：Markdown 渲染 -->
            <div v-else class="ai-message-content">
              <!-- 数据卡片（如果存在） -->
              <DataCard
                v-for="(card, idx) in msg.dataCards"
                :key="'card-' + idx"
                :card="card"
              />
              <!-- Markdown 正文 -->
              <MarkdownRenderer v-if="msg.content" :content="msg.content" />
            </div>
          </div>

          <!-- 流式输出时的思考状态 -->
          <div v-if="loading && streamingContent === '' && !aiStatus" class="ai-message ai-message--assistant">
            <div class="ai-message-avatar">
              <Icon name="Bot" :size="14" />
            </div>
            <div class="ai-message-content ai-typing">
              <span class="ai-typing-dots">
                <span class="ai-dot"></span>
                <span class="ai-dot"></span>
                <span class="ai-dot"></span>
              </span>
            </div>
          </div>

          <!-- AI 处理状态提示 -->
          <div v-if="loading && aiStatus" class="ai-message ai-message--assistant">
            <div class="ai-message-avatar">
              <Icon name="Bot" :size="14" />
            </div>
            <div class="ai-message-content ai-status-msg">
              <Icon name="Loader" :size="14" class="ai-status-spin" />
              <span>{{ aiStatus }}</span>
            </div>
          </div>

          <!-- 网络断开提示 -->
          <div v-if="connectionLost" class="ai-connection-lost">
            <Icon name="WifiOff" :size="14" />
            <span>连接中断，</span>
            <span class="ai-retry-link" @click="retryLastMessage">点击重试</span>
          </div>
        </div>

        <!-- 快捷指令（无消息时显示） -->
        <div class="ai-quick-commands" v-if="messages.length === 0 && quickCommands.length > 0">
          <div
            v-for="cmd in quickCommands"
            :key="cmd.label"
            class="ai-quick-cmd"
            @click="sendQuickCommand(cmd)"
          >
            <Icon :name="cmd.icon || 'Zap'" :size="14" />
            <span>{{ cmd.label }}</span>
          </div>
        </div>

        <!-- 输入区域 -->
        <div class="ai-input-area">
          <el-input
            v-model="inputText"
            placeholder="输入您的问题..."
            @keydown.enter.prevent="sendMessage"
            :disabled="loading"
            clearable
          />
          <el-button
            class="ai-send-btn"
            @click="sendMessage"
            :disabled="loading || !inputText.trim()"
            :loading="loading"
          >
            <Icon name="Send" :size="16" />
          </el-button>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref, nextTick, onMounted, onUnmounted } from 'vue'
import { ElMessageBox, ElMessage } from 'element-plus'
import {
  chatStream,
  getSessionList,
  createSession,
  deleteSession,
  getSessionHistory,
  getQuickCommands
} from '@/api/ai'
import Icon from '@/components/Icon/index.vue'
import MarkdownRenderer from './MarkdownRenderer.vue'
import DataCard from './DataCard.vue'

const visible = ref(false)
const inputText = ref('')
const loading = ref(false)
const messages = ref([])
const sessions = ref([])
const aiStatus = ref('') // AI 处理状态提示
const currentSessionId = ref(null)
const quickCommands = ref([])
const messagesRef = ref(null)

// T16: SSE 流式解析状态
const streamingContent = ref('')
const connectionLost = ref(false)
const lastUserMessage = ref('')
const abortController = ref(null)

onMounted(() => {
  loadQuickCommands()
})

onUnmounted(() => {
  // 清理：组件卸载时中止正在进行的请求
  if (abortController.value) {
    abortController.value.abort()
  }
})

function open() {
  visible.value = true
  loadSessions()
}

function close() {
  visible.value = false
}

// ============================================
// 会话管理
// ============================================

async function loadSessions() {
  try {
    const res = await getSessionList()
    sessions.value = res.data || []
    // 有会话但当前无选中 → 自动选第一个
    if (sessions.value.length > 0 && !currentSessionId.value) {
      currentSessionId.value = sessions.value[0].id
      loadHistory()
    }
  } catch (e) {
    console.error('加载会话列表失败:', e)
  }
}

async function loadHistory() {
  if (!currentSessionId.value) return
  try {
    const res = await getSessionHistory(currentSessionId.value)
    messages.value = (res.data || []).map(m => ({
      id: m.id,
      role: m.role,
      content: m.content,
      dataCards: m.dataCards || null
    }))
    scrollToBottom()
  } catch (e) {
    console.error('加载历史消息失败:', e)
  }
}

async function loadQuickCommands() {
  try {
    const res = await getQuickCommands()
    quickCommands.value = res.data || []
  } catch (e) {
    // 后端接口未就绪时使用默认快捷指令
    quickCommands.value = [
      { label: '今日经营概览', icon: 'BarChart3', prompt: '帮我查看今日的经营数据概览' },
      { label: '合同到期提醒', icon: 'FileText', prompt: '有哪些合同即将到期需要关注？' },
      { label: '欠费商户查询', icon: 'AlertCircle', prompt: '帮我查一下目前有哪些欠费商户' },
      { label: '铺位空置情况', icon: 'Building2', prompt: '当前各楼层铺位空置情况如何？' }
    ]
  }
}

// T18: 新建对话（移到标题栏按钮，更明显）
async function handleNewSession() {
  try {
    const res = await createSession({ title: '新对话' })
    const newId = res.data?.id || res.data
    await loadSessions()
    if (newId) {
      currentSessionId.value = newId
      messages.value = []
    }
    ElMessage.success('已创建新对话')
  } catch (e) {
    console.error('创建会话失败:', e)
    ElMessage.error('创建会话失败')
  }
}

// T18: 删除对话（带确认弹窗）
async function handleDeleteSession() {
  if (!currentSessionId.value) {
    ElMessage.warning('当前没有选中的会话')
    return
  }
  try {
    await ElMessageBox.confirm('确定要删除当前会话吗？删除后无法恢复。', '删除确认', {
      confirmButtonText: '删除',
      cancelButtonText: '取消',
      type: 'warning',
      confirmButtonClass: 'el-button--danger'
    })
    await deleteSession(currentSessionId.value)
    currentSessionId.value = null
    messages.value = []
    await loadSessions()
    // 自动切换到第一个会话
    if (sessions.value.length > 0) {
      currentSessionId.value = sessions.value[0].id
      loadHistory()
    }
    ElMessage.success('会话已删除')
  } catch (e) {
    if (e !== 'cancel') {
      console.error('删除会话失败:', e)
      ElMessage.error('删除会话失败')
    }
  }
}

function onSessionChange() {
  messages.value = []
  loadHistory()
}

// ============================================
// T16: SSE 流式解析 + 发送消息
// ============================================

async function sendMessage() {
  const text = inputText.value.trim()
  if (!text || loading.value) return

  // 如果没有会话，先创建一个
  if (!currentSessionId.value) {
    try {
      const res = await createSession({ title: text.slice(0, 20) })
      const newId = res.data?.id || res.data
      await loadSessions()
      currentSessionId.value = newId
    } catch (e) {
      console.error('创建会话失败:', e)
      messages.value.push({
        id: Date.now(),
        role: 'assistant',
        content: '抱歉，创建会话失败，请稍后重试。'
      })
      return
    }
  }

  inputText.value = ''
  loading.value = true
  connectionLost.value = false
  lastUserMessage.value = text
  streamingContent.value = ''

  // 添加用户消息
  messages.value.push({ id: Date.now(), role: 'user', content: text })

  // 添加空的助手消息占位
  const assistantMsg = {
    id: Date.now() + 1,
    role: 'assistant',
    content: '',
    dataCards: null
  }
  messages.value.push(assistantMsg)
  scrollToBottom()

  // 创建 AbortController 以支持取消
  abortController.value = new AbortController()

  try {
    const response = await chatStream(currentSessionId.value, text, abortController.value.signal)

    console.log('[AI] Response status:', response.status,
        'content-type:', response.headers.get('content-type'))

    if (!response.ok) {
      // 尝试读取错误响应体以获取具体信息
      let errorText = 'HTTP ' + response.status
      try {
        errorText = await response.text()
        console.error('[AI] Error response body:', errorText)
      } catch (ignored) {}
      throw new Error(errorText)
    }

    if (!response.body) {
      console.error('[AI] response.body is null')
      throw new Error('响应流不可用')
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()

    // SSE 解析状态
    let buffer = ''
    let currentEventName = '' // 追踪 event: 行
    let chunkCount = 0

    while (true) {
      const { done, value } = await reader.read()
      if (done) {
        console.log('[AI] Stream ended. Total chunks:', chunkCount,
            'content length:', assistantMsg.content.length)
        break
      }

      chunkCount++
      const chunk = decoder.decode(value, { stream: true })
      if (chunkCount <= 3) {
        console.log('[AI] Chunk #' + chunkCount + ':', JSON.stringify(chunk.substring(0, 300)))
      }
      buffer += chunk

      // 按双换行拆分 SSE 事件块
      const eventBlocks = buffer.split('\n\n')
      // 最后一个可能不完整，保留到 buffer
      buffer = eventBlocks.pop() || ''

      for (const block of eventBlocks) {
        if (!block.trim()) continue

        let eventName = ''
        let eventData = ''
        // SSE 标准要求：多个 data: 行用 \n 拼接
        const dataLines = []

        // 解析每个 SSE 事件块中的行
        const lines = block.split('\n')
        for (const line of lines) {
          if (line.startsWith('event:')) {
            eventName = line.slice(6).trim()
          } else if (line.startsWith('data:')) {
            dataLines.push(line.slice(5).trimStart())
          } else if (line.startsWith(':')) {
            // SSE 注释行，忽略
            continue
          } else if (line.startsWith('id:') || line.startsWith('retry:')) {
            continue
          }
        }
        // 多个 data 行拼接（SSE 标准）
        eventData = dataLines.join('\n')

        // 如果 event 行未出现但有 data 行，用空字符串作为默认事件名
        if (!eventName && eventData) {
          eventName = currentEventName || 'message'
        }
        if (eventName) {
          currentEventName = eventName
        }

        // 根据事件名分发处理
        if (eventName === 'token') {
          aiStatus.value = ''
          handleTokenEvent(eventData, assistantMsg)
        } else if (eventName === 'done') {
          aiStatus.value = ''
          handleDoneEvent(eventData, assistantMsg)
        } else if (eventName === 'error') {
          aiStatus.value = ''
          handleErrorEvent(eventData, assistantMsg)
        } else if (eventName === 'status') {
          aiStatus.value = eventData || ''
        } else if (eventName === 'dataCard') {
          handleDataCardEvent(eventData, assistantMsg)
        } else if (eventData) {
          handleGenericEvent(eventData, assistantMsg)
        }

        scrollToBottom()
      }
    }

    // 处理 buffer 中可能残留的数据
    if (buffer.trim()) {
      console.log('[AI] Remaining buffer:', JSON.stringify(buffer.substring(0, 300)))
      processRemainingBuffer(buffer, assistantMsg)
      scrollToBottom()
    }

    // 如果助手消息为空（后端未返回任何内容），给出提示
    if (!assistantMsg.content.trim() && !assistantMsg.dataCards) {
      console.warn('[AI] No content received after stream ended')
      assistantMsg.content = 'AI 服务暂未就绪，请稍后再试。'
    }

    // 刷新会话列表（标题可能已更新）
    loadSessions()
  } catch (e) {
    if (e.name === 'AbortError') {
      if (!assistantMsg.content.trim()) {
        assistantMsg.content = '已取消。'
      }
    } else {
      console.error('AI 对话异常:', e)
      connectionLost.value = true
      assistantMsg.content = '抱歉，服务异常，请稍后重试。'
    }
  } finally {
    loading.value = false
    streamingContent.value = ''
    aiStatus.value = ''
    abortController.value = null
    scrollToBottom()
  }
}

// event: token → 追加文本到 assistant 消息
function handleTokenEvent(data, assistantMsg) {
  if (!data || data === '[DONE]') return
  let text = data
  try {
    const parsed = JSON.parse(data)
    if (typeof parsed === 'string') {
      text = parsed
    } else if (parsed && typeof parsed === 'object') {
      text = parsed.content || parsed.token || parsed.text || parsed.data || ''
    }
  } catch (e) {
    // 非 JSON，直接作为纯文本追加
  }
  if (typeof text === 'string' && text) {
    assistantMsg.content += text
    streamingContent.value = assistantMsg.content
  }
}

// event: done → 完成对话
// done payload 是后端的权威最终内容（可能经过 fallback 修正，与 token 流累积内容不同）
// 只要 done 携带非空文本就覆盖累积内容，确保后端的纠错（如 [object Object] fallback）生效
function handleDoneEvent(data, assistantMsg) {
  if (data && data !== '[DONE]') {
    try {
      const parsed = JSON.parse(data)
      const text = typeof parsed === 'string' ? parsed
          : (parsed && parsed.content) || ''
      if (typeof text === 'string' && text) {
        assistantMsg.content = text
      }
    } catch (e) {
      if (data.trim()) {
        assistantMsg.content = data
      }
    }
  }
  streamingContent.value = ''
}

// event: error → 显示错误
function handleErrorEvent(data, assistantMsg) {
  let errorMsg = 'AI 服务出现错误'
  if (data) {
    try {
      const parsed = JSON.parse(data)
      errorMsg = typeof parsed === 'string' ? parsed
          : parsed.message || parsed.error || parsed.msg || data
    } catch (e) {
      errorMsg = data
    }
  }
  // 如果已有部分内容，追加错误信息；否则替换
  if (assistantMsg.content.trim()) {
    assistantMsg.content += '\n\n---\n**错误**: ' + errorMsg
  } else {
    assistantMsg.content = errorMsg
  }
}

// event: dataCard → 结构化数据卡片
function handleDataCardEvent(data, assistantMsg) {
  if (!data) return
  try {
    const card = JSON.parse(data)
    if (!assistantMsg.dataCards) {
      assistantMsg.dataCards = []
    }
    assistantMsg.dataCards.push(card)
  } catch (e) {
    console.warn('解析 dataCard 事件失败:', data)
  }
}

// 通用事件处理（message 等）
function handleGenericEvent(data, assistantMsg) {
  if (!data || data === '[DONE]') return
  try {
    const parsed = JSON.parse(data)
    if (typeof parsed === 'string') {
      assistantMsg.content += parsed
    } else if (parsed.content) {
      assistantMsg.content += parsed.content
    } else if (parsed.type === 'content' && parsed.data) {
      assistantMsg.content += parsed.data
    }
    streamingContent.value = assistantMsg.content
  } catch (e) {
    // 非 JSON，当作纯文本追加
    assistantMsg.content += data
    streamingContent.value = assistantMsg.content
  }
}

// 处理 buffer 中残留的不完整 SSE 块
function processRemainingBuffer(buffer, assistantMsg) {
  const lines = buffer.split('\n')
  let dataContent = ''
  for (const line of lines) {
    if (line.startsWith('data:')) {
      dataContent += line.slice(5).trim()
    }
  }
  if (dataContent && dataContent !== '[DONE]') {
    try {
      const parsed = JSON.parse(dataContent)
      const text = parsed.content || parsed.token || parsed.text || ''
      if (typeof text === 'string' && text) {
        assistantMsg.content += text
      }
    } catch (e) {
      assistantMsg.content += dataContent
    }
  }
}

// T16: 网络断开重试
function retryLastMessage() {
  connectionLost.value = false
  // 移除最后一条空的 assistant 消息
  if (messages.value.length > 0 && messages.value[messages.value.length - 1].role === 'assistant') {
    const lastMsg = messages.value[messages.value.length - 1]
    if (!lastMsg.content.trim()) {
      messages.value.pop()
    }
  }
  if (lastUserMessage.value) {
    inputText.value = lastUserMessage.value
    sendMessage()
  }
}

// 代码块复制（事件委托）
function handleMessagesClick(e) {
  const copyBtn = e.target.closest('.ai-code-copy')
  if (copyBtn) {
    const code = decodeURIComponent(copyBtn.getAttribute('data-code') || '')
    if (code && navigator.clipboard) {
      navigator.clipboard.writeText(code).then(() => {
        const original = copyBtn.textContent
        copyBtn.textContent = '已复制'
        setTimeout(() => { copyBtn.textContent = original }, 1500)
      }).catch(() => {
        ElMessage.warning('复制失败，请手动复制')
      })
    }
  }
}

function sendQuickCommand(cmd) {
  inputText.value = cmd.prompt
  sendMessage()
}

function scrollToBottom() {
  nextTick(() => {
    if (messagesRef.value) {
      messagesRef.value.scrollTop = messagesRef.value.scrollHeight
    }
  })
}
</script>

<script>
export default { name: 'AiSidebar' }
</script>

<!-- 非 scoped：teleport 到 body 后需要全局样式 -->
<style lang="scss">
/* ============================================
   气泡按钮
   ============================================ */
.ai-trigger-btn {
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: var(--el-color-primary);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 2000;
  transition: transform 0.2s, box-shadow 0.2s;

  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: scale(0.95);
  }
}

/* ============================================
   侧边面板
   ============================================ */
.ai-sidebar {
  position: fixed;
  top: 0;
  right: 0;
  width: 400px;
  height: 100vh;
  background: var(--el-bg-color);
  border-left: 1px solid var(--el-border-color-light);
  display: flex;
  flex-direction: column;
  z-index: 2001;
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.1);
}

/* ============================================
   标题栏
   ============================================ */
.ai-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid var(--el-border-color-light);
  flex-shrink: 0;

  .ai-header-left {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .ai-header-icon {
    color: var(--el-color-primary);
  }

  .ai-header-title {
    font-size: 15px;
    font-weight: 600;
    color: var(--el-text-color-primary);
  }

  .ai-header-actions {
    display: flex;
    align-items: center;
    gap: 4px;
  }

  .ai-header-action {
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    cursor: pointer;
    color: var(--el-text-color-regular);
    transition: all 0.2s;

    &:hover {
      background: var(--el-fill-color);
      color: var(--el-color-primary);
    }
  }
}

/* ============================================
   会话选择栏
   ============================================ */
.ai-session-bar {
  padding: 8px 16px;
  border-bottom: 1px solid var(--el-border-color-light);
  flex-shrink: 0;

  .el-select {
    width: 100%;
  }
}

/* ============================================
   消息列表
   ============================================ */
.ai-messages {
  flex: 1;
  overflow-y: auto;
  padding: 16px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--el-border-color);
    border-radius: 2px;

    &:hover {
      background: var(--el-text-color-placeholder);
    }
  }
}

/* 欢迎语 */
.ai-welcome {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  gap: 8px;

  .ai-welcome-icon {
    color: var(--el-color-primary);
    opacity: 0.6;
  }

  .ai-welcome-text {
    font-size: 14px;
    color: var(--el-text-color-primary);
    margin: 0;
    font-weight: 500;
  }

  .ai-welcome-sub {
    font-size: 12px;
    color: var(--el-text-color-placeholder);
    margin: 0;
  }
}

/* 单条消息 */
.ai-message {
  margin-bottom: 12px;
  display: flex;
  align-items: flex-start;
  gap: 8px;

  &--user {
    flex-direction: row-reverse;

    .ai-message-content {
      background: var(--el-color-primary);
      color: #fff;
      border-radius: 12px 12px 2px 12px;
    }
  }

  &--assistant {
    .ai-message-content {
      background: var(--el-fill-color-light);
      color: var(--el-text-color-primary);
      border-radius: 12px 12px 12px 2px;
    }
  }
}

.ai-message-avatar {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--el-fill-color);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: var(--el-color-primary);
}

.ai-message-content {
  display: inline-block;
  padding: 8px 12px;
  max-width: 85%;
  font-size: 14px;
  line-height: 1.6;
  word-break: break-word;

  /* assistant 消息内容不限制 white-space，让 Markdown 渲染控制 */
  .ai-message--assistant & {
    white-space: normal;
  }

  /* user 消息保持 pre-wrap */
  .ai-message--user & {
    white-space: pre-wrap;
  }
}

/* 思考动画（三点跳动） */
.ai-typing {
  .ai-typing-dots {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  .ai-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--el-text-color-placeholder);
    animation: ai-dot-bounce 1.2s ease-in-out infinite;

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }
}

/* AI 状态提示（带旋转图标） */
.ai-status-msg {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--el-text-color-secondary);
  font-size: 13px;
}

.ai-status-spin {
  animation: ai-spin 1s linear infinite;
}

@keyframes ai-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes ai-dot-bounce {
  0%, 60%, 100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  30% {
    transform: translateY(-6px);
    opacity: 1;
  }
}

/* 连接断开提示 */
.ai-connection-lost {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 12px;
  margin: 8px 0;
  background: var(--el-color-warning-light-9);
  border-radius: 8px;
  font-size: 12px;
  color: var(--el-color-warning-dark-2);

  .ai-retry-link {
    color: var(--el-color-primary);
    cursor: pointer;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
}

/* ============================================
   快捷指令
   ============================================ */
.ai-quick-commands {
  padding: 8px 16px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  flex-shrink: 0;
}

.ai-quick-cmd {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border: 1px solid var(--el-border-color);
  border-radius: 16px;
  cursor: pointer;
  font-size: 13px;
  color: var(--el-text-color-regular);
  transition: all 0.2s;

  &:hover {
    background: var(--el-fill-color);
    border-color: var(--el-color-primary);
    color: var(--el-color-primary);
  }
}

/* ============================================
   输入区域
   ============================================ */
.ai-input-area {
  padding: 12px 16px;
  border-top: 1px solid var(--el-border-color-light);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  .el-input {
    flex: 1;
  }

  .ai-send-btn {
    flex-shrink: 0;
    height: 32px;
    padding: 0 12px;
  }
}

/* ============================================
   过渡动画
   ============================================ */
.ai-slide-enter-active,
.ai-slide-leave-active {
  transition: transform 0.3s ease;
}

.ai-slide-enter-from,
.ai-slide-leave-to {
  transform: translateX(100%);
}

/* ============================================
   响应式：移动端全屏宽度
   ============================================ */
@media (max-width: 768px) {
  .ai-sidebar {
    width: 100vw;
  }

  .ai-trigger-btn {
    right: 16px;
    bottom: 16px;
    width: 44px;
    height: 44px;
  }
}
</style>
